import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SegmentedControl, type SegmentOption } from '../../components/ui';
import { useFeatures } from '../billing/plan';

/**
 * Shared date-range state for analytics surfaces (SPEC §3): Today, last 7
 * days, last 30 days, or a custom range. Ranges are computed in the browser's
 * local time and sent to the API as ISO instants; the server buckets days in
 * UTC (D-059).
 *
 * A custom period counts once both dates make a valid range: until then the
 * figures, and the heading over them, stay on the last valid period, and the
 * picker says what is missing (critique 2026-10-29, dashboard P1 #3). With
 * `inUrl`, the period lives in the address (`?period=custom&from=…&to=…`), so
 * a reload or a shared link shows the same figures.
 */

export type RangePreset = 'today' | '7d' | '30d' | 'custom';

/** What a custom period still lacks: two dates, the right order, or a span the server accepts. */
export type RangeProblem = 'pickTwo' | 'order' | 'tooLong';

export interface DateRangeState {
  /** The period the figures are for: the one picked, or the last valid one while a custom period is incomplete. */
  preset: RangePreset;
  /** The period picked in the control. */
  selected: RangePreset;
  setPreset: (preset: RangePreset) => void;
  customFrom: string;
  customTo: string;
  setCustomFrom: (value: string) => void;
  setCustomTo: (value: string) => void;
  /** ISO instants for the API, stable per selection. */
  params: { from: string; to: string };
  problem: RangeProblem | null;
}

interface Selection {
  preset: RangePreset;
  from: string;
  to: string;
}

const PRESETS: readonly RangePreset[] = ['today', '7d', '30d', 'custom'];
/** The server's limit (analytics `validateRange`). */
const MAX_RANGE_DAYS = 400;
const isDay = (value: string | null): value is string => value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value);

function problemOf({ preset, from, to }: Selection): RangeProblem | null {
  if (preset !== 'custom') return null;
  if (!from || !to) return 'pickTwo';
  if (from > to) return 'order';
  return (Date.parse(to) - Date.parse(from)) / 86_400_000 > MAX_RANGE_DAYS ? 'tooLong' : null;
}

function readUrl(search: URLSearchParams, initial: RangePreset): Selection {
  const period = search.get('period');
  const from = search.get('from');
  const to = search.get('to');
  return {
    preset: PRESETS.includes(period as RangePreset) ? (period as RangePreset) : initial,
    from: isDay(from) ? from : '',
    to: isDay(to) ? to : '',
  };
}

/** A day as an `<input type="date">` value, in local time. */
function dayInput(day: Date): string {
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
}

/** Today as an `<input type="date">` value, in local time. */
function todayInput(): string {
  return dayInput(new Date());
}

/**
 * The earliest start the plan's history allows (D-183): today − `days` + 1,
 * so a range starting that morning stays within the server's `now − days`.
 */
export function earliestStartInput(days: number, now: Date = new Date()): string {
  return dayInput(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1)));
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function useDateRange(initial: RangePreset = '30d', { inUrl = false }: { inUrl?: boolean } = {}): DateRangeState {
  const [search, setSearch] = useSearchParams();
  const [local, setLocal] = useState<Selection>({ preset: initial, from: '', to: '' });
  const selection = inUrl ? readUrl(search, initial) : local;
  const problem = problemOf(selection);

  const [lastValid, setLastValid] = useState<Selection>(() => (problem ? { preset: initial, from: '', to: '' } : selection));
  useEffect(() => {
    if (!problem) setLastValid({ preset: selection.preset, from: selection.from, to: selection.to });
  }, [problem, selection.preset, selection.from, selection.to]);
  const { preset, from: customFrom, to: customTo } = problem ? lastValid : selection;

  const update = (patch: Partial<Selection>) => {
    const next = { ...selection, ...patch };
    if (!inUrl) {
      setLocal(next);
      return;
    }
    setSearch(
      (previous) => {
        const params = new URLSearchParams(previous);
        for (const key of ['period', 'from', 'to']) params.delete(key);
        if (next.preset !== initial) params.set('period', next.preset);
        if (next.preset === 'custom') {
          if (next.from) params.set('from', next.from);
          if (next.to) params.set('to', next.to);
        }
        return params;
      },
      { replace: true },
    );
  };

  const params = useMemo(() => {
    // To the next whole minute: two visits in the same minute then share one
    // query key, so the dashboard opens from the cache instead of skeletons.
    const now = new Date(Math.ceil(Date.now() / 60_000) * 60_000);
    if (preset === 'custom' && customFrom && customTo) {
      const from = new Date(`${customFrom}T00:00:00`);
      const to = new Date(`${customTo}T23:59:59.999`);
      return { from: from.toISOString(), to: to.toISOString() };
    }
    const days = preset === 'today' ? 0 : preset === '7d' ? 6 : 29;
    const from = startOfLocalDay(new Date(now.getTime() - days * 86_400_000));
    // `now` is intentionally captured per selection change, not per render.
    return { from: from.toISOString(), to: now.toISOString() };
  }, [preset, customFrom, customTo]);

  return {
    preset,
    selected: selection.preset,
    setPreset: (next) => update({ preset: next }),
    customFrom: selection.from,
    customTo: selection.to,
    setCustomFrom: (value) => update({ from: value }),
    setCustomTo: (value) => update({ to: value }),
    params,
    problem,
  };
}

const DAY_MS = 86_400_000;
const PRESET_DAYS: Record<Exclude<RangePreset, 'custom'>, number> = { today: 1, '7d': 7, '30d': 30 };

/**
 * The window a KPI compares against: the same span just before, shifted by
 * whole days so "today so far" meets "yesterday at the same time".
 */
export function previousRange(range: Pick<DateRangeState, 'preset' | 'params'>): { from: string; to: string } {
  const from = Date.parse(range.params.from);
  const to = Date.parse(range.params.to);
  const shift = range.preset === 'custom' ? to - from + 1 : PRESET_DAYS[range.preset] * DAY_MS;
  return { from: new Date(from - shift).toISOString(), to: new Date(to - shift).toISOString() };
}

/** Catalogue keys per preset, under `period.heading`, `period.versus` and `period.same`. */
export const PRESET_KEY: Record<RangePreset, 'today' | 'd7' | 'd30' | 'custom'> = {
  today: 'today',
  '7d': 'd7',
  '30d': 'd30',
  custom: 'custom',
};

const DATE_INPUT =
  'h-[34px] rounded-ds-sm border border-border-strong bg-surface px-2.5 text-ds-sm text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600';

/** Today / 7 days / 30 days / Custom: one segmented control, with the two dates once Custom is picked. */
export function RangePicker({ range }: { range: DateRangeState }) {
  const { t } = useTranslation();
  const problemId = useId();
  const today = todayInput();
  // The plan's history (D-183), read from the workspace every page already loads.
  const { maxAnalyticsDays } = useFeatures();
  const earliest = maxAnalyticsDays !== null ? earliestStartInput(maxAnalyticsDays) : undefined;
  const describedBy = range.problem ? problemId : undefined;
  const presets: SegmentOption<RangePreset>[] = [
    { value: 'today', label: t('period.today') },
    { value: '7d', label: t('period.d7') },
    { value: '30d', label: t('period.d30') },
    { value: 'custom', label: t('period.custom') },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl label={t('period.label')} tone="soft" options={presets} value={range.selected} onChange={range.setPreset} />
      {range.selected === 'custom' ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-ds-sm">
            <input
              type="date"
              aria-label={t('period.from')}
              aria-describedby={describedBy}
              aria-invalid={range.problem === 'order' || undefined}
              value={range.customFrom}
              min={earliest}
              max={range.customTo || today}
              onChange={(e) => range.setCustomFrom(e.target.value)}
              className={DATE_INPUT}
            />
            <span className="text-ink-3">{t('period.between')}</span>
            <input
              type="date"
              aria-label={t('period.to')}
              aria-describedby={describedBy}
              aria-invalid={range.problem === 'order' || undefined}
              value={range.customTo}
              min={range.customFrom || undefined}
              max={today}
              onChange={(e) => range.setCustomTo(e.target.value)}
              className={DATE_INPUT}
            />
          </div>
          {range.problem ? (
            <p id={problemId} className="text-ds-xs text-ink-3">
              {t(`period.problem.${range.problem}`)}
            </p>
          ) : null}
          {maxAnalyticsDays !== null ? (
            <p className="text-ds-xs text-ink-3">
              {t('period.historyHint', { days: maxAnalyticsDays })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Sur les 7 derniers jours" with the period selector on the same line, right
 * above the figures it controls (R01, R29, R30; A01-04, A12-04): the same
 * place on every page that has one.
 */
export function PeriodBar({ range }: { range: DateRangeState }) {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <div role="group" aria-labelledby={headingId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5">
      <h2 id={headingId} className="text-ds-lg text-ink">
        {t(`period.heading.${PRESET_KEY[range.preset]}`)}
      </h2>
      <RangePicker range={range} />
    </div>
  );
}
